import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Prompt } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from 'react-bootstrap';
import { format, min, parse } from 'date-fns';
import clone from 'clone';
import { SOLR_DELAY } from '../../../../constants';
import Header from '../../../components/Header/Header';
import { loadCampaigns } from '../../../actions/campaignActions';
import {
  getCampaignSchedule,
  updateCampaignSchedule,
} from '../../../actions/campaignScheduleActions';
import { addNotification } from '../../../actions/notificationActions';
import Spinner from '../../../components/Spinner/Spinner';
import AssetSelector from '../../../components/AssetSelector/AssetSelector';
import ErrorPage from '../ErrorPage/ErrorPage';
import ToolTipIcon from '../../../components/ToolTipIcon/ToolTipIcon';
import CampaignScheduleEntriesTable from '../../../components/CampaignSchedule/CampaignScheduleEntriesTable';
import errorMessage from '../../../utils/errorMessage';
import { closeFilters } from '../../../actions/filterActions';

export class CampaignSchedulePage extends Component {
  state = {
    entries: [],
    dirty: false,
    showCampaignSelector: false,
  };

  componentDidMount() {
    this.loadData();
    document.addEventListener('visibilitychange', this.loadData);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const {
      campaignSchedule: { loading },
    } = this.props;
    const {
      campaignSchedule: {
        loading: nextLoading,
        campaignSchedule: { campaignScheduleEntries },
      },
    } = nextProps;

    if (loading && !nextLoading && campaignScheduleEntries) {
      this.setState({ entries: campaignScheduleEntries });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('visibilitychange', this.loadData);
    const { closeFilters } = this.props;

    closeFilters();
  }

  loadData = () => {
    if (document.visibilityState === 'hidden') {
      return;
    }

    const { loadCampaigns, getCampaignSchedule } = this.props;
    const { dirty } = this.state;

    loadCampaigns('?count=100000000');

    if (!dirty) {
      getCampaignSchedule();
    }
  };

  showCampaignSelector = () => this.setState({ showCampaignSelector: true });

  hideCampaignSelector = () => this.setState({ showCampaignSelector: false });

  onClickCampaignSelectorItem = item => {
    const { addNotification } = this.props;
    const { entries } = this.state;
    const newEntries = clone(entries);
    const entry = {
      campaignId: +item.id,
      campaignName: item.name,
      campaignDescription: item.description,
      campaignParent: item.parent,
      campaignTags: item.tags ? item.tags.join(',') : null,
      date: format(new Date(), 'yyyyMMdd'),
      time: '1200',
    };

    newEntries.push(entry);
    this.setState({ entries: newEntries, dirty: true });
    addNotification({ type: 'success', text: `${item.name} added to the entries.` });

    return entry;
  };

  buildCampaignScheduleForServer = () => {
    const { entries } = this.state;

    return {
      campaignScheduleEntries: entries.map(item => ({
        campaignId: item.campaignId,
        date: item.date,
        time: item.time,
      })),
    };
  };

  cancel = () => {
    const {
      campaignSchedule: {
        campaignSchedule: { campaignScheduleEntries },
      },
    } = this.props;

    this.setState({
      entries: campaignScheduleEntries,
      dirty: false,
    });
  };

  update = () => {
    const { addNotification, getCampaignSchedule } = this.props;
    const schedule = this.buildCampaignScheduleForServer();

    return updateCampaignSchedule(schedule).then(
      () => {
        addNotification({ type: 'success', text: 'Campaign Schedule updated.' });
        this.setState({ dirty: false });
        setTimeout(getCampaignSchedule, SOLR_DELAY);
      },
      err => addNotification({ type: 'danger', text: errorMessage(err) }),
    );
  };

  onChangeDate = (value, index) => {
    const { entries } = this.state;
    const newEntries = clone(entries);

    newEntries[index].date = value;
    this.setState({ entries: newEntries, dirty: true });
  };

  onChangeHour = (event, index) => {
    const { entries } = this.state;
    const newEntries = clone(entries);

    newEntries[index].time = event.target.value + newEntries[index].time.slice(2, 4);
    this.setState({ entries: newEntries, dirty: true });
  };

  onChangeMinute = (event, index) => {
    const { entries } = this.state;
    const newEntries = clone(entries);

    newEntries[index].time = newEntries[index].time.slice(0, 2) + event.target.value;
    this.setState({ entries: newEntries, dirty: true });
  };

  onRemoveEntry = index => {
    const { entries } = this.state;
    const newEntries = clone(entries);

    newEntries.splice(index, 1);
    this.setState({ entries: newEntries, dirty: true });
  };

  loneCampaignWarning = () => {
    const { entries } = this.state;

    if (entries.length === 1) {
      return (
        <div className="mx-auto mt-4 max-w-full mb-4">
          <div className="group relative flex items-start space-x-3 rounded-xl border border-orange-100 bg-orange-50/50 p-2 transition-all duration-300 hover:shadow-sm">
            <div className="mt-0.5 flex-shrink-0">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-orange-100/80 text-orange-600">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                  stroke="currentColor"
                  className="h-5 w-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                  />
                </svg>
              </div>
            </div>

            <div className="flex-1">
              <p className="mt-1 text-sm leading-relaxed text-orange-700/80 !mb-0">
                You must have at least{' '}
                <span className="font-medium text-orange-900 underline decoration-orange-200 underline-offset-2">
                  one campaign
                </span>{' '}
                in your schedule. If you wish to remove the campaign shown below, you must first add
                another to replace it.
              </p>
            </div>
          </div>
        </div>
      );
    }
  };

  hasActiveCampaign = () => {
    const { entries } = this.state;
    const dates = entries.map(item => parse(item.date, 'yyyyMMdd', new Date()));

    return min(dates) < new Date();
  };

  render() {
    const {
      auth,
      campaigns: {
        campaigns: { campaigns },
      },
      campaignSchedule: { loading, error },
    } = this.props;
    const { dirty, entries, showCampaignSelector } = this.state;
    const {
      user: {
        permissions: { Writer },
      },
    } = auth;

    if (error) {
      return <ErrorPage error={error} />;
    }

    return (
      <div className="page-structure !bg-star-light min-h-screen">
        <Helmet title="Campaign Schedule" />
        <Header />
        <div className="mt-8 !bg-star-light">
          <div className="page-content page-campaign-schedule">
            <Spinner loading={loading}>
              <Prompt
                when={dirty}
                message="You have unsaved changes. Are you sure you want to leave this page?"
              />
              <form>
                {Writer && (
                  <ToolTipIcon
                    className="add-btn"
                    toolText="Add Campaign"
                    onClick={this.showCampaignSelector}
                  >
                    <img
                      src="/assets/images/icons/upload_icon.svg"
                      alt="upload icon"
                      className="w-[40px] h-[40px] cursor-pointer"
                    />
                  </ToolTipIcon>
                )}

                {this.loneCampaignWarning()}
                <CampaignScheduleEntriesTable
                  auth={auth}
                  data={entries}
                  onChangeDate={this.onChangeDate}
                  onChangeHour={this.onChangeHour}
                  onChangeMinute={this.onChangeMinute}
                  onRemoveEntry={this.onRemoveEntry}
                />
                {Writer && (
                  <div className="form-buttons !items-center !flex !justify-center">
                    <Button
                      className="!bg-dull/30 !rounded-[15px] !text-[#0A364B] poppins-bold !font-[500]"
                      onClick={this.cancel}
                    >
                      Discard Changes
                    </Button>
                    <Button
                      onClick={this.update}
                      disabled={!dirty || !this.hasActiveCampaign()}
                      className="!rounded-[15px] !bg-btn-primary-form !text-white poppins-bold !font-[500]"
                      data-test="update-campaign-schedule-btn"
                    >
                      Update Campaign Schedule
                    </Button>
                  </div>
                )}
              </form>
            </Spinner>
          </div>
        </div>
        <AssetSelector
          type="campaign"
          title="Add Campaigns to the Campaign Schedule"
          show={showCampaignSelector}
          onHide={this.hideCampaignSelector}
          onSuccess={this.onClickCampaignSelectorItem}
          assets={campaigns}
        />
      </div>
    );
  }
}

CampaignSchedulePage.propTypes = {
  auth: PropTypes.object.isRequired,
  campaigns: PropTypes.object.isRequired,
  campaignSchedule: PropTypes.object.isRequired,
  loadCampaigns: PropTypes.func.isRequired,
  getCampaignSchedule: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  closeFilters: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  auth: state.auth,
  campaigns: state.campaigns,
  campaignSchedule: state.campaignSchedule,
});
const mapDispatchToProps = {
  loadCampaigns,
  getCampaignSchedule,
  addNotification,
  closeFilters,
};

export default connect(mapStateToProps, mapDispatchToProps)(CampaignSchedulePage);
